import React, { useState, useEffect, Fragment } from 'react';
import { Container, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { reset } from 'redux-form';
import { setDay, startOfMonth, endOfMonth } from 'date-fns';
import { showNotificationAutoDestroy } from '../../shared/components/ShowNotification';
import {
  changeLoadingTrue,
  changeLoadingFalse,
} from '../../redux/actions/loadingActions';
import {
  calendarListByFilter,
  saveCalendarMutation,
  eventCalendarListByFilter,
  removeCalendarMutation,
} from '../../shared/queries/Calendar';
import CalendarLabels from './components/CalendarLabels';
import BigCalendar from './components/BigCalendar';
import GraphQl from '../../GraphQL';
import FullScreenDialog from '../../shared/components/FullScreenDialog';
import CalendarForm from './components/CalendarForm';
import {
  getDateWithoutTime,
  getUTCDateFromMilliseconds,
} from '../../shared/utils/smartutils';
import EventFormHandler from './components/EventFormHandler';

const Calendar = (props) => {
  const { t, dispatch } = props;
  const [calendarScreen, setCalendarScreen] = useState(false);
  const [calendarList, setCalendarList] = useState([]);
  const [eventScreen, setEventScreen] = useState(false);
  const [dtoEvent, setDtoEvent] = useState({});
  const [dtoCalendar, setDtoCalendar] = useState({});
  const [calendarIds, setCalendarIds] = useState([]);
  const [period, setPeriod] = useState({});
  const [events, setEvents] = useState([]);

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const toggleCalendarScreen = () => {
    dispatch(reset('CalendarScreenForm'));
    setDtoCalendar({});
    setCalendarScreen(!calendarScreen);
  };

  const toggleEventScreen = () => {
    dispatch(reset('EventScreenForm'));
    setEventScreen(!eventScreen);
  };

  const onSelectCalendar = (values) => {
    toggleCalendarScreen();
    setDtoCalendar({ ...values });
  };

  const onSelectEvent = (values) => {
    let updatedDtoEvent;
    if (values.id) {
      updatedDtoEvent = {
        ...values,
        communication: {
          ...values.communication,
          htmlContent:
            values.communication.htmlContent || values.communication.content,
        },
        allDay: values.allDay,
        requestPresence: values.requestPresence,
        repeat: values.repeat,
        daysRepeat: values.daysRepeat,
        local: values.local,
        calendar: {
          value: values.calendar.id,
          label: t(`${values.calendar.name}`),
        },
      };
    } else {
      updatedDtoEvent = {
        ...values,
        initialDate: values.start,
        finalDate: values.start,
        editable: true,
        allDay: false,
        attendanceConfirmation: false,
        repeat: false,
        communication: {
          htmlContent: '',
          title: '',
          id: 0,
        },
      };
    }
    setDtoEvent(updatedDtoEvent);
    toggleEventScreen();
  };

  const loadAllCalendar = () => {
    const filter = {};
    const query = calendarListByFilter(filter);
    showLoading();
    GraphQl(query, {})
      .then((res) => {
        res.calendarListByFilter = res.calendarListByFilter.map((item) => ({
          ...item,
          visibility: {
            value: item.visibility,
            label: t(`calendar.visibility.${item.visibility}`),
          },
        }));
        setCalendarList(res.calendarListByFilter);
        const ids = res.calendarListByFilter.map((item) => ({ id: item.id }));
        setCalendarIds(ids);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const loadAllEvents = () => {
    const query = eventCalendarListByFilter(period, calendarIds);
    showLoading();
    GraphQl(query, {})
      .then((res) => {
        res.eventCalendarListByFilter = res.eventCalendarListByFilter
          .filter((item) => item.lstEventCalendar.length !== 0)
          .map((item) => item.lstEventCalendar)
          .reduce((arr, item) => arr.concat(item), [])
          .map((item) => ({
            ...item,
            id: item.id,
            title: item.communication.title,
            start: item.allDay
              ? getDateWithoutTime(item.initialDate)
              : getUTCDateFromMilliseconds(item.initialDate),
            startHour: getUTCDateFromMilliseconds(item.initialDate),
            end: item.allDay
              ? getDateWithoutTime(item.finalDate)
              : getUTCDateFromMilliseconds(item.finalDate),
            endHour: getUTCDateFromMilliseconds(item.finalDate),
            calendar: {
              id: item.calendar.id,
              name: item.calendar.name,
              color: item.calendar.colorHexa,
            },
            description: item.communication.content,
            inicialDateRepeat: item.inicialDateRepeat,
            finalDateRepeat: item.finalDateRepeat,
            daysRepeat: item.daysRepeat,
            fatherCode: item.fatherCode,
            repeat: item.repeat,
          }));
        setEvents(res.eventCalendarListByFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const handleSubmitCalendarModal = (values) => {
    const mutation = saveCalendarMutation(values);
    toggleCalendarScreen();
    dispatch(reset('CalendarScreenForm'));
    showLoading();
    GraphQl(mutation, {})
      .then(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
        loadAllCalendar();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const removeCalendar = (id) => {
    const mutation = removeCalendarMutation(id);
    toggleCalendarScreen();
    showLoading();
    GraphQl(mutation, {})
      .then(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-removed')
        );
        loadAllCalendar();
      })
      .catch(() => {
        hideLoading();
      });
  };

  const foreignToggleCloseEventForm = () => {
    toggleEventScreen();
    loadAllEvents();
  };

  const onRangeChange = (view) => {
    let initialPeriod;
    let finalPeriod;
    let updatedPeriod = { ...period };
    if (view.start && view.end) {
      initialPeriod = new Date(view.start).getTime();
      finalPeriod = new Date(view.end).getTime();
      updatedPeriod = { initialPeriod, finalPeriod };
    }
    if (view[view.length - 1]) {
      if (view.length > 1) {
        const [start] = view;
        const end = view[view.length - 1];
        initialPeriod = new Date(start).getTime();
        finalPeriod = new Date(end).getTime();
        updatedPeriod = { initialPeriod, finalPeriod };
      } else {
        const [start] = view;
        initialPeriod = new Date(start).getTime();
        updatedPeriod = { initialPeriod, finalPeriod: initialPeriod };
      }
    }
    setPeriod(updatedPeriod);
  };

  const getRangeView = () => {
    const initialDayOfMonth = startOfMonth(new Date());
    const finalDayOfMonth = endOfMonth(new Date());
    const initialDayOfWeek = setDay(initialDayOfMonth, 0).getTime();
    const finalDayOfWeek = setDay(finalDayOfMonth, 6).getTime();
    setPeriod({ initialPeriod: initialDayOfWeek, finalPeriod: finalDayOfWeek });
  };

  const onCheckedCalendar = (checked, { id }) => {
    let updated = [...calendarIds];
    if (checked === true) {
      updated = [...calendarIds, { id }];
    } else {
      const calendarIdRemove = updated.findIndex((item) => item.id === id);
      updated.splice(calendarIdRemove, 1);
    }
    setCalendarIds(updated);
  };

  useEffect(() => {
    loadAllCalendar();
    getRangeView();
  }, []);

  useEffect(() => {
    if (Object.keys(period).length !== 0 && calendarIds.length > 0) {
      loadAllEvents();
    }
  }, [calendarIds, period]);

  useEffect(() => {
    setDtoCalendar(dtoCalendar);
  }, [dtoCalendar]);

  return (
    <Fragment>
      <Container>
        <FullScreenDialog
          title={t('panels.calendars')}
          toggleDialog={toggleCalendarScreen}
          isOpen={calendarScreen}
        >
          <CalendarForm
            dto={dtoCalendar}
            onSubmit={handleSubmitCalendarModal}
            removeItemClickHandler={removeCalendar}
          />
        </FullScreenDialog>
        <FullScreenDialog
          title={t('screens.mnt_evento_calendario.title')}
          toggleDialog={toggleEventScreen}
          isOpen={eventScreen}
        >
          {dtoEvent && (
            <EventFormHandler
              foreignToggleClose={foreignToggleCloseEventForm}
              event={dtoEvent}
            />
          )}
        </FullScreenDialog>
        <Row>
          <BigCalendar
            selectEvent={onSelectEvent}
            onRangeChange={onRangeChange}
            events={events}
          />
          <CalendarLabels
            calendarList={calendarList}
            toggle={toggleCalendarScreen}
            onSelectCalendar={onSelectCalendar}
            onCheckedCalendar={onCheckedCalendar}
          />
        </Row>
      </Container>
    </Fragment>
  );
};

Calendar.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(Calendar)
);
